import { attr, html, raw } from './html.js';
import { applyPrintOptions, DEFAULT_PRINT_OPTIONS, resolvePrintLocale } from './print-options.js';
import { documentDirection } from './strings.js';
import { DOCUMENT_CSS } from './styles.js';
import { creditNoteHtml } from './templates/credit-note.js';
import { deliveryNoteHtml } from './templates/delivery-note.js';
import { goodsReceiptHtml } from './templates/goods-receipt.js';
import { importOrderHtml } from './templates/import-order.js';
import { partnerStatementHtml } from './templates/partner-statement.js';
import { purchaseInvoiceHtml } from './templates/purchase-invoice.js';
import { purchaseOrderHtml } from './templates/purchase-order.js';
import { purchaseReturnHtml } from './templates/purchase-return.js';
import { quotationHtml } from './templates/quotation.js';
import { reportHtml } from './templates/report.js';
import { salesInvoiceHtml } from './templates/sales-invoice.js';
import { salesOrderHtml } from './templates/sales-order.js';
export * from './format.js';
export * from './html.js';
export * from './print-options.js';
export * from './strings.js';
export * from './styles.js';
export * from './types.js';
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONE RENDERER — DECISION.md §2.13's seam, and the reason there is no
 * second template to drift.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §2.13 asks for generation to sit behind `renderDocument(type, locale, data)
 * -> Buffer`. That signature splits cleanly in two, and the split is what buys
 * the guarantee:
 *
 *     renderDocumentHtml(input) -> string      ← HERE, and shared
 *     renderDocument(input)     -> Buffer      ← apps/api/src/documents/,
 *                                                = Chromium(renderDocumentHtml)
 *
 * BOTH CONSUMERS CALL THIS FUNCTION.
 *
 *  - The human pressing **Print** gets this string written into a print
 *    iframe (`apps/web/src/lib/print-document.ts`) and the browser prints it.
 *  - The **PDF** job gets this string handed to a headless Chromium page and
 *    `page.pdf()` turns it into bytes.
 *
 * So "the PDF is generated from the same rendered HTML a human would print"
 * is not a convention anybody has to remember — it is the only HTML that
 * exists. Neither caller owns a template.
 *
 * WHY NOT DRIVE CHROMIUM AT THE LIVE SPA URL instead, which would be even more
 * literally "the same page"? Because it would make every PDF depend on a
 * signed-in browser session for a user the worker is not, on the SPA build
 * being deployed and reachable from the worker, and on a React render
 * completing before a screenshot deadline — three moving parts, each of which
 * fails by producing a blank or half-rendered invoice rather than an error. A
 * pure function from data to markup fails by throwing, and can be asserted on
 * in a unit test with no browser at all.
 *
 * A NOTE ON TRUST. Every interpolation here is escaped (see html.ts). That
 * matters more than usual for the browser path: this markup is written into an
 * iframe in the signed-in app, so an unescaped item description would be
 * script execution on the app's own origin.
 *
 * THE PRINT OPTIONS (INV-H6) are part of the input, not a layer either caller
 * adds afterwards — see print-options.ts for why. `print.language` is
 * resolved here, before anything is rendered, so a company that fixes its
 * paper to Arabic gets an Arabic document from an English screen AND from the
 * worker, from one line.
 */
export function renderDocumentHtml(input) {
    const { company, generatedAt, generatedBy, payload } = input;
    const print = input.print ?? DEFAULT_PRINT_OPTIONS;
    const locale = resolvePrintLocale(input.locale, print.language);
    const dir = documentDirection(locale);
    const body = (() => {
        switch (payload.kind) {
            case 'sales_invoice':
                return salesInvoiceHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'partner_statement':
                return partnerStatementHtml(payload.data, locale, company, generatedAt, generatedBy);
            case 'report':
                return reportHtml(payload.data, locale, company, generatedAt, generatedBy);
            // Wave 2 — every record kind takes the invoice's full signature, print
            // options included, so INV-H6's company-wide choices reach all of them.
            case 'quotation':
                return quotationHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'sales_order':
                return salesOrderHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'delivery_note':
                return deliveryNoteHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'credit_note':
                return creditNoteHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'purchase_order':
                return purchaseOrderHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'purchase_invoice':
                return purchaseInvoiceHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'goods_receipt':
                return goodsReceiptHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'purchase_return':
                return purchaseReturnHtml(payload.data, locale, company, generatedAt, generatedBy, print);
            case 'import_order':
                return importOrderHtml(payload.data, locale, company, generatedAt, generatedBy, print);
        }
    })();
    // The stylesheet and the caller's @font-face rules are markup THIS package
    // and its two adapters produced, never request data — the only two `raw()`
    // call sites outside html.ts, and both are here where they can be seen.
    const style = raw(`${input.fontFaceCss ?? ''}\n${DOCUMENT_CSS}`);
    const document = `<!doctype html>
<html lang="${attr(locale)}" dir="${attr(dir)}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${documentTitle(input)}</title>
<style>
:root { --doc-font: ${locale === 'ar' ? "'IBM Plex Sans Arabic', 'Noto Naskh Arabic', system-ui, sans-serif" : "'IBM Plex Sans', system-ui, -apple-system, sans-serif"}; }
${style.value}
</style>
</head>
<body>
${body.value}
</body>
</html>`;
    return applyPrintOptions(document, print, locale);
}
/**
 * The window/tab title and, for the browser print path, the filename the print
 * dialog suggests. Escaped like everything else — a customer called `<script>`
 * is a valid customer name.
 */
function documentTitle(input) {
    const { payload } = input;
    switch (payload.kind) {
        case 'sales_invoice':
            return html `${payload.data.code}`.value;
        case 'partner_statement':
            return html `${payload.data.partnerCode} ${payload.data.asOf}`.value;
        case 'report':
            return html `${payload.data.title} ${payload.data.from} ${payload.data.to}`.value;
        case 'quotation':
        case 'sales_order':
        case 'delivery_note':
        case 'credit_note':
        case 'purchase_order':
        case 'purchase_invoice':
        case 'goods_receipt':
        case 'purchase_return':
        case 'import_order':
            return html `${payload.data.code}`.value;
    }
}
/**
 * The filename a stored artifact gets. Deliberately ASCII and boring: it
 * becomes a `Content-Disposition` value and an object key suffix, and a
 * customer name in either script is not something to put in either.
 */
export function documentFilename(input) {
    const safeReference = input.reference.replace(/[^A-Za-z0-9._-]+/g, '-').slice(0, 60) || 'document';
    return `${input.kind}-${safeReference}-${input.locale}.pdf`;
}
//# sourceMappingURL=index.js.map